import { SWRConfig } from 'swr';
import { unstable_serialize } from 'swr/infinite';
import { useEffect } from 'react';
import fetchPageMetaInfo from '@api/fetchers/pageMetaInfo';
import Head from 'next/head';
import SEO from '@seo/index';
import { useSetSelectedNav } from '@stores/sidenav';
import Landing from '@cont/FreeLiveClasses/FreeLiveClasses';
import { getKey as getTopologyKey } from '@api/hooks/topology/useTopology';
import { fetchGoalInfo } from '@api/fetchers/goal';
import {
  fetchSpecialClassesFeed,
  getFeedAPI
} from '@api/fetchers/free-live-classes';
import subGoalSchema from '@seo/goal/subGoal';
import pushRoute from '@utils/pushRoute';
import { FREE_CLASSES_AND_TESTS } from '@constants/segment';
import Layout from '@layout/index';
import fetchIsGoalGTP from '@api/fetchers/constants';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';
import { usePageSeoContent } from '@stores/AppCommonData';
import { getCustomPageDetails } from '@utils/getCustomPageDetails';
import validateAndSetSessionId, {
  DEVICE_ID_COOKIE_NAME
} from '@utils/session/validateAndSetSessionId';
import cookies, { readCookie } from '@utils/cookies';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsFreeUser, getIsLoggedIn } from '@api/selectors/auth';
import { fetchABExperimentData } from '@api/fetchers/experiments';
import { abProxyJWT } from '@utils/abHelper';
import { setExperimentVariation } from '@stores/experiments';

const FreeLiveClassesPageWithSEO = ({ goal, querySlug }) => {
  const { slug, title: goalTitle, uid, ogImage } = goal || {};

  const pageMetaInfo = getCustomPageDetails(
    goal?.name,
    goal?.title,
    FREE_CLASSES_AND_TESTS
  );
  const { title, description } = usePageSeoContent();
  const metaTitle = title || `Free Live Classes | ${goalTitle} | Unacademy`;
  const metaDescription =
    description ||
    `Prepare live from Top Educators with comprehensive, structured and crash courses along with practice section for ${goalTitle} on Unacademy Plus.`;
  const canonicalURL = `https://unacademy.com/goal/${slug}/${uid}/classes`;
  const imageURL =
    ogImage ||
    'https://static.uacdn.net/static/dist/c76c2e18c7980818a913f9467c74c567.png';

  useSetSelectedNav('classes');

  const isFreeUser = useMyInfo(getIsFreeUser(goal?.uid));
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const accessToken = readCookie('accessToken');

  useEffect(() => {
    if (querySlug !== slug)
      pushRoute(
        '/goal/[goalSlug]/[goalUID]/classes',
        `/goal/${slug}/${uid}/classes`,
        { shallow: true }
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    validateAndSetSessionId();
    const deviceID = cookies.readCookie(DEVICE_ID_COOKIE_NAME);
    if (!accessToken || (accessToken && isLoggedIn && isFreeUser)) {
      fetchABExperimentData(
        'notify_me_and_follow_educator_component',
        abProxyJWT,
        deviceID
      );
    } else if (accessToken && isLoggedIn && !isFreeUser) {
      setExperimentVariation('notify_me_and_follow_educator_component', null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, isLoggedIn, isFreeUser]);

  return (
    <>
      <SEO
        title={metaTitle}
        description={metaDescription}
        openGraph={{
          title: metaTitle,
          description: metaDescription,
          images: [
            {
              url: imageURL
            }
          ],
          url: canonicalURL
        }}
        schema={subGoalSchema({
          goal,
          description: metaDescription,
          subGoal: { name: 'Free classes & tests', link: 'classes' }
        })}
      />
      <Head>
        <title>{metaTitle}</title>
      </Head>
      <Landing goal={goal} pageMetaInfo={pageMetaInfo} />
    </>
  );
};

const FreeLiveClassesPageWithSWRConfig = ({
  goal,
  classesFeedFallbackData
}) => {
  const key = unstable_serialize(() => getFeedAPI({ goalUID: goal.uid }));

  return (
    <SWRConfig
      value={{
        revalidateOnMount: false,
        dedupingInterval: 60 * 1000,
        fallback: {
          [key]: [classesFeedFallbackData]
        }
      }}
    >
      <FreeLiveClassesPageWithSEO goal={goal} />
    </SWRConfig>
  );
};

FreeLiveClassesPageWithSWRConfig.Layout = Layout;

FreeLiveClassesPageWithSWRConfig.Wrapper = PageReduxProvider;

FreeLiveClassesPageWithSWRConfig.pageInfo = {
  isMWebSupported: true,
  name: FREE_CLASSES_AND_TESTS,
  layoutOptions: {
    showPlusNav: true
  },
  isCMSMeta: true
};

export default FreeLiveClassesPageWithSWRConfig;

export const getStaticProps = async ({ params }) => {
  try {
    const { goalUID, goalSlug } = params;
    const promises = [
      fetchGoalInfo(goalUID),
      fetchIsGoalGTP(goalUID),
      fetchSpecialClassesFeed({ goalUID }),
      fetchPageMetaInfo(FREE_CLASSES_AND_TESTS, goalUID)
    ];

    const [goal, isGTP, classesFeedFallbackData, pageMetaInfo] =
      await Promise.all(promises);

    if (!goal?.uid) {
      return {
        redirect: {
          destination: '/'
        }
      };
    }

    if (isGTP) {
      return {
        redirect: {
          destination: `/goal/gtp/${goalSlug}/${goalUID}`
        }
      };
    }

    if (goal?.isPlatformGoal) {
      return {
        redirect: {
          destination: `/goal/${goalSlug}/${goalUID}`
        }
      };
    }

    return {
      props: {
        goal,
        classesFeedFallbackData,
        pageMetaInfo,
        globalFallbackData: {
          [getTopologyKey(goal.uid)]: goal,
          metaInfo: pageMetaInfo
        }
      },
      revalidate: 5 * 60
    };
  } catch (err) {
    return { notFound: true, revalidate: 60 };
  }
};

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  };
};
